import { FilterSelect } from '@/features/common/FilterPanel/FilterSelect';
import { useCategoryLabels } from '@/features/common/useCategoryLabels';
import { ListingFilterPanel } from '@/features/publicsite/ListingFilterPanel';
import { activeFilterTags } from '@/features/common/listingFilters';
import { categoryFilterOptions, newsFilters } from '@/features/publicsite/news/newsFilters';
import { STORY_TYPE } from '@/features/publicsite/news/storyType';
import type { NewsFilterPanelProps } from '@/features/publicsite/types/news/NewsFilterPanelProps';
import { publicNews } from '@/i18n/dictionaries/publicNews';
import { useTranslation } from '@/i18n/useTranslation';

/** The lists that get a tag under the bar when set, in the order the bar shows them. */
const TAGGED = ['category', 'type'] as const;

/**
 * /stiri's filter bar: the one the calendar has (ListingFilterPanel) — no frame, a tag under it for
 * each list that is set, "Șterge filtrele" in place of "Resetează" — with the two lists particular
 * to the news, a category and a type. With the period they fill the bar's one row, so nothing folds
 * behind "Mai multe filtre" as the calendar's kind and organiser do. Everything lives in the URL — a
 * filtered list can be shared, and Back undoes a filter — which is the convention /biblioteca set
 * for public filters.
 */
export function NewsFilterPanel({
  params,
  categories,
  results,
  onFilter,
  onReset,
}: NewsFilterPanelProps) {
  const { t } = useTranslation(publicNews);
  const categoryName = useCategoryLabels();
  const filters = newsFilters(params);
  // Each list's choices once: its dropdown offers them, and its tag names the one picked.
  const choices = {
    category: categoryFilterOptions(
      categories,
      filters.category,
      t('filterAllCategories'),
      categoryName,
    ),
    type: [
      { value: '', label: t('filterAllTypes') },
      { value: STORY_TYPE.news, label: t('typeNewsPlural') },
      { value: STORY_TYPE.article, label: t('typeArticlePlural') },
    ],
  };
  const tags = activeFilterTags(TAGGED, filters, choices).map((tag) => ({
    ...tag,
    onRemove: () => onFilter({ [tag.name]: null }),
  }));

  return (
    <ListingFilterPanel
      ariaLabel={t('filtersAria')}
      filters={filters}
      canReset={Object.keys(filters).length > 0}
      results={results}
      onFilter={onFilter}
      onReset={onReset}
      variant="plain"
      tags={tags}
    >
      <FilterSelect
        name="category"
        label={t('filterCategory')}
        ariaLabel={t('filterCategoryAria')}
        placeholder={t('filterAllCategories')}
        options={choices.category}
        value={filters.category ?? ''}
        onChange={(v) => onFilter({ category: v })}
        // Off only when there is nothing to choose and nothing to take off.
        disabled={categories.length === 0 && !filters.category}
        labelHidden
      />
      <FilterSelect
        name="type"
        label={t('filterType')}
        ariaLabel={t('filterTypeAria')}
        placeholder={t('filterAllTypes')}
        options={choices.type}
        value={filters.type ?? ''}
        onChange={(v) => onFilter({ type: v })}
        labelHidden
      />
    </ListingFilterPanel>
  );
}
